import { useState, useEffect, useMemo } from "react";
import { Megaphone, Gift, Truck, Clock, Bell, Star } from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL || "/";

interface PromoStripCfg {
  promoStripEnabled?: string;
  promoStripPhrasesAr?: string | null;
  promoStripPhrasesEn?: string | null;
  /** Per-language phrases map: { langCode: "phrase1\nphrase2" }. When set,
   *  takes precedence over the legacy promoStripPhrasesAr / promoStripPhrasesEn
   *  fields. Other lang codes (fr, ur, …) are only stored here. */
  promoStripPhrasesI18n?: Record<string, string> | null;
  promoStripBgColor?: string;
  promoStripTextColor?: string;
  promoStripFontSize?: string;
  promoStripFontWeight?: string;
  promoStripIcon?: string;
  /** Pipe-separated per-phrase icon ids matching `promoStripPhrasesAr` order. */
  promoStripIconsAr?: string | null;
  promoStripRotateMs?: string;
  promoStripPosition?: string;
  // Theme fallbacks: when the merchant has not set a dedicated promo bg/text
  // color, we paint the strip in the active theme template colors so applying
  // a new template (e.g. Natural Green) retints the strip automatically.
  topBarBg?: string;
  topBarTextColor?: string;
  headerBg?: string;
  headerTextColor?: string;
}

type IconRenderer = (style: React.CSSProperties) => React.ReactNode;

const PROMO_ICON_MAP: Record<string, IconRenderer> = {
  megaphone: (s) => <Megaphone className="shrink-0" style={s} />,
  gift: (s) => <Gift className="shrink-0" style={s} />,
  truck: (s) => <Truck className="shrink-0" style={s} />,
  clock: (s) => <Clock className="shrink-0" style={s} />,
  bell: (s) => <Bell className="shrink-0" style={s} />,
  star: (s) => <Star className="shrink-0" style={s} />,
};

export function PromoStrip({ position = "top" }: { position?: "top" | "bottom" }) {
  const [promo, setPromo] = useState<PromoStripCfg | null>(null);
  const [idx, setIdx] = useState(0);

  // CLS prevention: read the --nx-promo-h CSS variable set by the lcp-hint
  // inline script in index.html. If the server told us the promo strip will
  // be visible (height > 0), we render an invisible placeholder of that height
  // immediately on mount — before the theme/settings API responds. This ensures
  // the strip occupies its space from the first React render, so the real
  // content appearing later doesn't shift the page.
  // Only relevant for position="top" (the strip that appears above the fold).
  const [hintHeight] = useState<number>(() => {
    if (position !== "top" || typeof window === "undefined") return 0;
    const v = getComputedStyle(document.documentElement).getPropertyValue("--nx-promo-h").trim();
    const n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : 0;
  });

  useEffect(() => {
    // `cache: "no-store"` defeats the browser HTTP cache so a merchant
    // editing the promo strip in admin sees the new font size / colors
    // on the storefront immediately after saving, instead of waiting for
    // the cached GET response to expire.
    fetch(`${BASE_URL}api/theme/settings`, { cache: "no-store" })
      .then(r => r.json())
      .then(data => { if (data && typeof data === "object") setPromo(data as PromoStripCfg); })
      .catch(() => {});
  }, []);

  // Current storefront language from the <html lang> attribute (e.g. "ar", "en", "fr").
  const currentLang = typeof document !== "undefined" ? (document.documentElement.lang || "ar") : "ar";
  const isEnglish = currentLang === "en";

  const phrases = useMemo(() => {
    // 1. Try the new per-language i18n map first (supports any language code).
    const i18n = promo?.promoStripPhrasesI18n;
    if (i18n && typeof i18n === "object" && i18n[currentLang]) {
      const list = String(i18n[currentLang]).split(/[\n|]+/).map(s => s.trim()).filter(Boolean);
      if (list.length > 0) return list;
    }
    // 2. Legacy fallback: EN column for English, AR column otherwise.
    if (isEnglish) {
      const enRaw = promo?.promoStripPhrasesEn ?? "";
      const enList = String(enRaw).split(/[\n|]+/).map(s => s.trim()).filter(Boolean);
      if (enList.length > 0) return enList;
    }
    const raw = promo?.promoStripPhrasesAr ?? "";
    return String(raw).split(/[\n|]+/).map(s => s.trim()).filter(Boolean);
  }, [promo?.promoStripPhrasesAr, promo?.promoStripPhrasesEn, promo?.promoStripPhrasesI18n, currentLang, isEnglish]);

  // Per-phrase icon list parsed in lockstep with phrases. Pipe-separated;
  // empty or unknown values fall back to the global `promoStripIcon`.
  const phraseIcons = useMemo(() => {
    const raw = promo?.promoStripIconsAr ?? "";
    return String(raw).split("|").map(s => s.trim());
  }, [promo?.promoStripIconsAr]);

  useEffect(() => {
    if (phrases.length < 2) return;
    const ms = Number(promo?.promoStripRotateMs || "4000") || 4000;
    const t = setInterval(() => setIdx(i => (i + 1) % phrases.length), ms);
    return () => clearInterval(t);
  }, [phrases.length, promo?.promoStripRotateMs]);

  if (!promo) {
    // While waiting for theme/settings: hold space if the server hint says the
    // strip will be visible. This is an invisible spacer that prevents the page
    // from shifting when the real strip appears.
    if (hintHeight > 0 && position === "top") {
      return <div style={{ height: hintHeight }} aria-hidden="true" />;
    }
    return null;
  }
  if (promo.promoStripEnabled === "false" || phrases.length === 0) return null;
  const cfgPos = (promo.promoStripPosition === "bottom" ? "bottom" : "top") as "top" | "bottom";
  if (cfgPos !== position) return null;

  const fontPx = Number(promo.promoStripFontSize || "13") || 13;
  // Icon scales ~1.25× the font height so it stays balanced as the
  // merchant changes font size in admin (no more tiny icon next to large text).
  const iconPx = Math.round(fontPx * 1.25);
  const fallbackIcon = promo.promoStripIcon || "truck";
  const phraseIconKey = (phraseIcons[idx % Math.max(phraseIcons.length, 1)] || "").trim() || fallbackIcon;
  const renderIcon = PROMO_ICON_MAP[phraseIconKey] || PROMO_ICON_MAP[fallbackIcon] || PROMO_ICON_MAP.truck;

  return (
    <div
      className="flex items-center justify-center gap-2 py-2 px-4 text-center transition-colors w-full"
      style={{
        backgroundColor: promo.promoStripBgColor || promo.topBarBg || promo.headerBg || "#fdf6ec",
        color: promo.promoStripTextColor || promo.topBarTextColor || promo.headerTextColor || "#1f2937",
        fontSize: `${fontPx}px`,
        fontWeight: (promo.promoStripFontWeight || "600") as React.CSSProperties["fontWeight"],
      }}
      dir={isEnglish ? "ltr" : "rtl"}
    >
      {renderIcon({ width: iconPx, height: iconPx })}
      <span key={idx} className="animate-in fade-in duration-500">
        {phrases[idx % phrases.length]}
      </span>
    </div>
  );
}
